import { GlobalOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateProfile } from '../../fetchHooks/updateProfile';
import { useUserProfile } from '../../fetchHooks/useUserProfile';

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { data: userProfile } = useUserProfile();
  const queryClient = useQueryClient();

  const changeLanguage = (lng: string) => {
    // Change the UI language immediately
    i18n.changeLanguage(lng);

    // Update the user profile with the new language preference
    if (userProfile?.id) {
      updateProfile({ language: lng, id: userProfile.id }, () => {
        // Invalidate and refetch user profile to get updated data
        void queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      });
    }
  };

  // Sync language with user profile when component mounts or user profile changes
  useEffect(() => {
    if (userProfile?.language && userProfile.language !== i18n.language) {
      i18n.changeLanguage(userProfile.language);
    }
  }, [userProfile?.language, i18n]);

  const getCurrentLanguageName = () => {
    switch (i18n.language) {
      case 'en':
        return 'English';
      case 'nb':
        return 'Norsk';
      default:
        return 'English';
    }
  };

  const getLanguageFlag = (lng: string) => {
    switch (lng) {
      case 'en':
        return '🇺🇸';
      case 'nb':
        return '🇳🇴';
      default:
        return '🇺🇸';
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'en',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '4px 0',
            fontWeight: i18n.language === 'en' ? 600 : 400,
            color: i18n.language === 'en' ? '#1890ff' : 'inherit',
          }}
        >
          <span style={{ fontSize: '16px' }}>{getLanguageFlag('en')}</span>
          <span>English</span>
          {i18n.language === 'en' && <span style={{ marginLeft: 'auto', fontSize: '12px' }}>✓</span>}
        </div>
      ),
      onClick: () => changeLanguage('en'),
    },
    {
      key: 'nb',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '4px 0',
            fontWeight: i18n.language === 'nb' ? 600 : 400,
            color: i18n.language === 'nb' ? '#1890ff' : 'inherit',
          }}
        >
          <span style={{ fontSize: '16px' }}>{getLanguageFlag('nb')}</span>
          <span>Norsk</span>
          {i18n.language === 'nb' && <span style={{ marginLeft: 'auto', fontSize: '12px' }}>✓</span>}
        </div>
      ),
      onClick: () => changeLanguage('nb'),
    },
  ];

  return (
    <Dropdown
      menu={{ items }}
      placement="bottomRight"
      trigger={['click']}
      arrow={{ pointAtCenter: true }}
      overlayStyle={{ minWidth: '140px' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          color: 'inherit',
          border: '1px solid transparent',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <GlobalOutlined
          style={{
            fontSize: '16px',
            transition: 'transform 0.2s ease',
          }}
        />
        <span
          style={{
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0.3px',
          }}
        >
          {getCurrentLanguageName()}
        </span>
      </div>
    </Dropdown>
  );
};
